import { useEffect, useRef } from 'react';
import useSound from 'use-sound';
import { useUnmount } from 'react-use';
import { OnlybotsScene } from '@/lib/types';
import titleMusic from '@/sounds/ob-music-title.mp3';
import codeInputMusic from '@/sounds/ob-music-codebox.mp3';
import storyMusic from '@/sounds/ob-music-story-sequence.mp3';
import verificationMusic from '@/sounds/ob-music-confirmation-and-login.mp3';
import quizMusic from '@/sounds/ob-music-quizzing.mp3';
import { useMute } from '@/hooks/useMute';

const howlerConfig = {
    loop: true,
};
export const useMusic = (userFlowStep: OnlybotsScene): void => {
    // music where preload == true
    const [playTitleMusic, { stop: stopTitleMusic }] = useSound(titleMusic, { loop: true });

    // music where preload == 'metadata'
    const [playCodeInputMusic, { stop: stopCodeInputMusic }] = useSound(codeInputMusic, howlerConfig);
    const [playStoryMusic, { stop: stopStoryMusic }] = useSound(storyMusic, howlerConfig);
    const [playVerificationMusic, { stop: stopVerificationMusic }] = useSound(verificationMusic, howlerConfig);
    const [playQuizMusic, { stop: stopQuizMusic }] = useSound(quizMusic, howlerConfig);
    const verificationMusicPlaying = useRef<boolean>(false);
    const quizMusicPlaying = useRef<boolean>(false);
    const { soundEnabled } = useMute();

    useEffect(() => {
        if (soundEnabled && userFlowStep === 'TITLE') {
            playTitleMusic();
        } else {
            stopTitleMusic();
        }

        if (soundEnabled && (userFlowStep === 'QUIZ' || userFlowStep === 'PASSEDQUIZ')) {
            if (!quizMusicPlaying.current) {
                playQuizMusic();
                quizMusicPlaying.current = true;
            }
        } else {
            stopQuizMusic();
            quizMusicPlaying.current = false;
        }

        if (
            soundEnabled &&
            (userFlowStep === 'CONNECT' || userFlowStep === 'BEGINTEST' || userFlowStep === 'PERFORMRECAPTCHA')
        ) {
            if (!verificationMusicPlaying.current) {
                playVerificationMusic();
                verificationMusicPlaying.current = true;
            }
        } else {
            stopVerificationMusic();
            verificationMusicPlaying.current = false;
        }

        if (soundEnabled && userFlowStep === 'STORY') {
            playStoryMusic();
        } else {
            stopStoryMusic();
        }

        if (soundEnabled && userFlowStep === 'CODEINPUT') {
            playCodeInputMusic();
        } else {
            stopCodeInputMusic();
        }
    }, [
        playCodeInputMusic,
        playQuizMusic,
        playStoryMusic,
        playTitleMusic,
        playVerificationMusic,
        stopCodeInputMusic,
        stopQuizMusic,
        stopStoryMusic,
        stopTitleMusic,
        stopVerificationMusic,
        soundEnabled,
        userFlowStep,
    ]);

    useUnmount(() => {
        stopTitleMusic();
        stopQuizMusic();
        stopVerificationMusic();
        stopStoryMusic();
        stopCodeInputMusic();
    });
};
